import { stepsToText, type CalcStep } from "./steps";
import { csvCell } from "@/lib/csv";
import { tableToCsv, tableToTsv, type TableColumn } from "./table";

/**
 * A table plus the way to export it, typed once where it is built (`reportTable`) and passed to CalcReport.
 * The Row type is erased here on purpose so one component can show any calculator's table.
 */
export interface ReportTable {
  caption: string;
  /** Short heading shown above the table (the caption is for screen readers). */
  heading: string;
  rows: unknown[];
  columns: TableColumn<any>[]; // eslint-disable-line @typescript-eslint/no-explicit-any
  rowKey: (row: any) => string | number; // eslint-disable-line @typescript-eslint/no-explicit-any
}

export function reportTable<Row>(t: { caption: string; heading: string; rows: Row[]; columns: TableColumn<Row>[]; rowKey: (row: Row) => string | number }): ReportTable {
  return t as unknown as ReportTable;
}

/** The text a calculator copies: title, one-line summary, working, each table (as tab-separated text), notes and the site line. */
export function reportText(r: { title: string; summary: string; steps: CalcStep[]; tables: ReportTable[]; notes?: string[] }): string {
  return [
    r.title, r.summary, "",
    ...(r.steps.length ? ["How this was calculated", stepsToText(r.steps), ""] : []),
    ...r.tables.flatMap((t) => [t.heading, tableToTsv(t.rows, t.columns), ""]),
    ...(r.notes?.length ? [...r.notes, ""] : []),
    "Calculated with FreeDailyPro.com",
  ].join("\n");
}

/** One CSV for the report: the first table, or every table one after the other, each under its own heading row. */
export function reportCsv(tables: ReportTable[]): string {
  if (tables.length === 1) return tableToCsv(tables[0].rows, tables[0].columns);
  const BOM = "﻿";
  return BOM + tables.map((t) => `${csvCell(t.heading)}\r\n${tableToCsv(t.rows, t.columns).replace(BOM, "")}`).join("\r\n");
}
